'use client'

import { Capacitor, registerPlugin } from '@capacitor/core'
import { isPostHogInitialized } from '@/instrumentation-client'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

/**
 * Informe que el nativo dejó guardado cuando el proceso de renderizado del
 * WebView murió (ver android/…/RenderProcessGoneListener.java).
 *
 * Existe porque el arreglo nos deja ciegos: al gestionar el evento en nativo, el
 * crash deja de llegar a Play Console, así que esta es la única traza que queda.
 */
export interface RenderProcessGoneReport {
  /** true cuando el arranque anterior terminó con el render process muerto. */
  recovered: boolean
  /** true = crasheó Chromium; false = lo mató el sistema (OOM). El discriminante. */
  didCrash?: boolean
  /** `RenderProcessGoneDetail.rendererPriorityAtExit()`; -1 si no se pudo leer. */
  rendererPriority?: number
  /** Epoch ms del dispositivo en el momento de la muerte. */
  occurredAt?: number
}

export interface WebViewRecoveryPlugin {
  consumeLastRenderProcessGone: () => Promise<RenderProcessGoneReport>
}

export const WebViewRecovery = registerPlugin<WebViewRecoveryPlugin>('WebViewRecovery')

export function isWebViewRecoveryAvailable(): boolean {
  if (typeof window === 'undefined') return false

  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('WebViewRecovery')
}

export interface WebViewRecoveryReportDeps {
  plugin?: WebViewRecoveryPlugin
  isAvailable?: () => boolean
  now?: () => number
}

function buildProperties(report: RenderProcessGoneReport, now: number): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    did_crash: report.didCrash === true,
    renderer_priority_at_exit: report.rendererPriority ?? -1,
  }

  if (typeof report.occurredAt === 'number' && report.occurredAt > 0) {
    properties.reported_after_seconds = Math.max(0, Math.round((now - report.occurredAt) / 1000))
  }

  return properties
}

/**
 * Emite a PostHog la muerte del render process del arranque anterior, si hubo.
 * Devuelve true solo cuando se reportó algo.
 */
export async function reportWebViewRecovery(
  deps: WebViewRecoveryReportDeps = {}
): Promise<boolean> {
  const { plugin = WebViewRecovery, isAvailable = isWebViewRecoveryAvailable, now = Date.now } = deps

  if (!isAvailable()) return false

  // No consumimos el informe hasta que PostHog pueda emitirlo: el nativo lo
  // conserva y se reporta en el siguiente arranque, en vez de perderse aquí.
  if (!isPostHogInitialized()) return false

  try {
    const report = await plugin.consumeLastRenderProcessGone()
    if (!report?.recovered) return false

    captureProductEvent(
      PRODUCT_EVENTS.WEBVIEW_RENDER_PROCESS_GONE,
      buildProperties(report, now())
    )
    return true
  } catch {
    // El arranque de la app nunca puede depender de esta telemetría.
    return false
  }
}
